import { EventEmitter } from 'events';
import fallbackPageLoaderResponse from '../mocks/fallbackPageLoaderResponse.json';
import fallbackSeoLoaderResponse from '../mocks/fallbackSeoLoaderResponse.json';
import { magnoliaAPI } from '@/services/api/magnolia/magnoliaAPI';
import { reportDtCustomError } from 'br2s-ui-common-utils-02';
import { MAGNOLIA_FETCH_ERROR } from '../constants/dynatrace';

export const events = new EventEmitter();

export function getMagnoliaHost() {
  return process.env.REACT_APP_MGNL_IS_PREVIEW! === 'true' ? 
    process.env.REACT_APP_MGNL_AUTHOR_HOST! :
    process.env.REACT_APP_MGNL_PUBLIC_HOST;
}

export function getApiBase() {
  return process.env.REACT_APP_MGNL_IS_PREVIEW! === 'true' ? 
    (`${process.env.REACT_APP_MGNL_AUTHOR_HOST}/${process.env.REACT_APP_MGNL_AUTHOR_BASE}`):
    (`${process.env.REACT_APP_MGNL_PUBLIC_HOST}/${process.env.REACT_APP_MGNL_PUBLIC_BASE}`);
}

export async function getContent(contentPath?: string, config?: {}) {
  const apiBase = getApiBase();
  const pagesResPath = `${apiBase}/${process.env.REACT_APP_MGNL_API_PAGES}${contentPath}`;
  
  const pagesRes = await magnoliaAPI.getContent(pagesResPath, config);

  if (pagesRes?.status !== 200) {
    throw new Error(`${pagesRes.status} ${pagesRes.statusText}`);
  }

  return pagesRes.data;
}

export async function getPageContent(contentPath?: string, config?: {}) {
  const apiBase = getApiBase();
  const pagesResPath = `${apiBase}/${process.env.REACT_APP_MGNL_API_PAGES}${contentPath}`;

  try {
    const pagesRes = await magnoliaAPI.getContent(pagesResPath, config);
    if (pagesRes?.status !== 200) {
      throw new Error(`${pagesRes.status} ${pagesRes.statusText}`);
    }

    return pagesRes.data;
  } catch(error) {
    console.error(error);
    reportDtCustomError(
      MAGNOLIA_FETCH_ERROR,
      'Could not fetch Magnolia page content.',
      `Page content path: ${pagesResPath} | Error: ${JSON.stringify(error)}`,
      );
  
    return fallbackPageLoaderResponse;
  }
}

export async function getPageSeo(pagePath?: string, config?: {}) {
  const apiBase = getApiBase();
  const pageSeoResPath = `${apiBase}/${process.env.REACT_APP_MGNL_API_SEO}${pagePath}`;
  try {
    const pageSeoRes = await magnoliaAPI.getContent(pageSeoResPath, config);
    if (pageSeoRes?.status !== 200) {
      throw new Error(`${pageSeoRes.status} ${pageSeoRes.statusText}`);
    }

    return pageSeoRes.data;
  } catch(error: any) {
    if (error?.response?.status === 404) {
      try {
        const pageDefaultSeoRes = await getPageDefaultSeo();
        return pageDefaultSeoRes;
      } catch(error: any) {
        return fallbackSeoLoaderResponse;
      }
    }

    return fallbackSeoLoaderResponse;
  }
}

export async function getPageDefaultSeo() {
  const apiBase = getApiBase();
  const pageDefaultSeoResPath = `${apiBase}/${process.env.REACT_APP_MGNL_API_SEO}`;

  try {
    const pageDefaultSeoRes = await magnoliaAPI.getContent(pageDefaultSeoResPath);
    return pageDefaultSeoRes.data;
  } catch(error: any) {
    console.error(error);
    reportDtCustomError(
      MAGNOLIA_FETCH_ERROR,
      'Could not fetch Default Magnolia page SEO data.',
      `Page SEO path: ${pageDefaultSeoResPath} | Error: ${JSON.stringify(error)}`,
    );
  }
}

export async function getTemplateAnnotations(templateAnnotationsPath?: string, config?: {}) {
  const apiBase = getApiBase();
  const templateAnnotationsResPath = `${apiBase}/${process.env.REACT_APP_MGNL_API_TEMPLATES}${templateAnnotationsPath}`;

  try {
    const templateAnnotationsRes = await magnoliaAPI.getContent(templateAnnotationsResPath, config);

    if (templateAnnotationsRes?.status !== 200) {
      throw new Error(`${templateAnnotationsRes.status} ${templateAnnotationsRes.statusText}`);
    }

    return templateAnnotationsRes.data;
  } catch(error) {
    console.error(error);
    reportDtCustomError(
      MAGNOLIA_FETCH_ERROR,
      'Could not fetch Magnolia template annotations.',
      `Template annotations path: ${templateAnnotationsResPath} | Error: ${JSON.stringify(error)}`,
      );
  
    return fallbackPageLoaderResponse;
  }
}

/**
 * WIP start
 * @param data 
 * @param area 
 * @returns 
 */
export const getAreaNodes = (data: any, area?: string) => {
  if (!Object.keys(data).length && data['@nodes'].length) return
  let componentNodes = {}, allAreas = []

  if (area) return data[area]

  for (const element of data['@nodes']) {
      componentNodes = {...componentNodes, [element]: data[element]}
  }

  return componentNodes
}

export interface ComponentDef {
  "@name"?: string
  "@path"?: string
  "@id"?: string
  "@nodeType"?: string
  "mgnl:lastModified"?: string
  "mgnl:created"?: string
  "@nodes"?: string[]
  [key: string]: any
}

export const getAreaComponents = (components: ComponentDef) => {
  if (typeof components['@nodes'] === 'undefined' || !components['@nodes']?.length) return components;
  return components['@nodes'].map((i: string) => components[i]);
}
